import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Plus, Mail, Trash2, Building2, Check, X, Copy, MessageCircle, Users, UserMinus, Search } from "lucide-react";
import { Link } from "react-router-dom";

export const CompaniesSection = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [companies, setCompanies] = useState([]);
  const [accountants, setAccountants] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", nif: "", contact_email: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteSending, setInviteSending] = useState(false);
  const [assignOpen, setAssignOpen] = useState(null);
  const [assignSel, setAssignSel] = useState(new Set());
  const [assignSaving, setAssignSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [clientsOpen, setClientsOpen] = useState(null);
  const [clientsList, setClientsList] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [delInvoices, setDelInvoices] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [search, setSearch] = useState("");

  const filteredCompanies = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    const digits = q.replace(/\D/g, "");
    return companies.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (digits && (c.nif || "").includes(digits)) ||
      (c.contact_email || "").toLowerCase().includes(q)
    );
  })();

  const load = async () => {
    const { data } = await api.get("/companies");
    setCompanies(data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (isAdmin) api.get("/accountants").then(({ data }) => setAccountants(data)).catch(() => {});
  }, [isAdmin]);

  const accName = (id) => accountants.find((a) => a.id === id)?.name || "—";

  const create = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await api.post("/companies", form);
      setForm({ name: "", nif: "", contact_email: "", vat_regime: "monthly" });
      setShowForm(false);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteOpen) return;
    setDeleting(true);
    try {
      await api.delete(`/companies/${deleteOpen.id}`);
      toast.success(`Empresa "${deleteOpen.name}" apagada.`);
      setDeleteOpen(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setDeleting(false); }
  };

  const openClients = async (c) => {
    setClientsOpen(c);
    setClientsList([]);
    setDelInvoices(false);
    setClientsLoading(true);
    try {
      const { data } = await api.get(`/companies/${c.id}/clients`);
      setClientsList(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setClientsLoading(false); }
  };

  const removeClient = async (cl) => {
    setRemovingId(cl.id);
    try {
      const { data } = await api.delete(`/users/clients/${cl.id}`, { params: { delete_invoices: delInvoices } });
      toast.success(`Utilizador "${cl.name || cl.email}" removido.${data.deleted_invoices ? ` ${data.deleted_invoices} fatura(s) apagada(s).` : ""}`);
      setClientsList((prev) => prev.filter((x) => x.id !== cl.id));
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setRemovingId(null); }
  };

  const openInvite = (c) => {
    setInviteOpen(c);
    setInviteEmail(c.contact_email || "");
    setInviteResult(null);
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviteSending(true);
    try {
      const { data } = await api.post("/invites", { company_id: inviteOpen.id, email: inviteEmail });
      setInviteResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setInviteSending(false); }
  };

  const openAssign = (c) => {
    setAssignOpen(c);
    setAssignSel(new Set(c.accountant_ids || []));
  };

  const toggleAssign = (id) => {
    setAssignSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const saveAssign = async () => {
    setAssignSaving(true);
    try {
      await api.patch(`/companies/${assignOpen.id}/accountants`, { accountant_ids: [...assignSel] });
      setAssignOpen(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setAssignSaving(false); }
  };

  return (
    <div data-testid="companies-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-display text-2xl">Empresas</h2>
          <p className="text-[color:var(--ink-muted)] mt-2 max-w-xl">Registo das empresas que a Perfinco acompanha. Só estas podem entrar na app — através de convite enviado por email.</p>
        </div>
        <button onClick={() => setShowForm((v) => !v)} className="btn-primary inline-flex items-center gap-2" data-testid="toggle-company-form-btn">
          <Plus size={16} /> Nova empresa
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="card-soft mt-6 grid sm:grid-cols-3 gap-4 items-end" data-testid="company-form">
          <div>
            <label className="label-eyebrow block mb-2">Nome</label>
            <input className="input-soft" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="company-name-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">NIF (9 dígitos)</label>
            <input className="input-soft font-mono-data" value={form.nif} onChange={(e) => setForm({...form, nif: e.target.value.replace(/\D/g,"")})} maxLength={9} required data-testid="company-nif-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Email de contacto</label>
            <input type="email" className="input-soft" value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} required data-testid="company-email-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Regime de IVA</label>
            <select className="input-soft" value={form.vat_regime} onChange={(e) => setForm({...form, vat_regime: e.target.value})} data-testid="company-vat-regime-select">
              <option value="monthly">Mensal</option>
              <option value="quarterly">Trimestral</option>
            </select>
          </div>
          {error && <div className="sm:col-span-3 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-lg p-3">{error}</div>}
          <div className="sm:col-span-3 flex gap-2">
            <button type="submit" disabled={saving} className="btn-primary" data-testid="create-company-btn">{saving ? "A criar…" : "Criar empresa"}</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancelar</button>
          </div>
        </form>
      )}

      <div className="mt-6">
        <div className="relative max-w-md">
          <input
            className="input-soft pr-10"
            placeholder="Pesquisar por nome ou NIF…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="company-search-input"
          />
          {search ? (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]" data-testid="company-search-clear" title="Limpar">
              <X size={16} />
            </button>
          ) : (
            <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-muted)] pointer-events-none" />
          )}
        </div>
        <p className="text-sm text-[color:var(--ink-muted)] mt-2" data-testid="company-count">
          {filteredCompanies.length} {filteredCompanies.length === 1 ? "empresa" : "empresas"}{search && ` de ${companies.length}`}
        </p>
      </div>

      <div className="mt-4 space-y-3">
        {companies.length === 0 ? (
          <div className="card-soft text-center py-12 text-[color:var(--ink-muted)]" data-testid="empty-companies">
            <Building2 className="mx-auto" size={28} />
            <p className="mt-3">Ainda sem empresas registadas.</p>
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="card-soft text-center py-12 text-[color:var(--ink-muted)]" data-testid="no-search-results">
            <Search className="mx-auto" size={28} />
            <p className="mt-3">Sem empresas para “{search}”.</p>
          </div>
        ) : filteredCompanies.map((c) => (
          <div key={c.id} className="card-soft flex flex-wrap items-center gap-4" data-testid={`company-row-${c.id}`}>
            <div className="w-12 h-12 rounded-xl bg-[color:var(--paper)] border border-[color:var(--border)] flex items-center justify-center">
              <Building2 size={20} className="text-[color:var(--primary)]" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-medium">{c.name}</div>
              <div className="text-sm text-[color:var(--ink-muted)] font-mono-data">NIF {c.nif} · {c.contact_email}</div>
              {isAdmin && (
                <div className="text-xs text-[color:var(--ink-muted)] mt-1" data-testid={`company-accountants-${c.id}`}>
                  Responsáveis: {(c.accountant_ids && c.accountant_ids.length)
                    ? c.accountant_ids.map(accName).join(", ")
                    : <span className="text-[#991B1B]">nenhum atribuído</span>}
                </div>
              )}
            </div>
            <div className="flex gap-6 text-sm">
              <div><div className="label-eyebrow">Utilizadores</div><div className="font-mono-data text-lg">{c.users_count}</div></div>
              <div><div className="label-eyebrow">Faturas</div><div className="font-mono-data text-lg">{c.invoices_count}</div></div>
            </div>
            <div className="flex gap-2">
              {isAdmin && (
                <button onClick={() => openAssign(c)} className="btn-secondary text-sm inline-flex items-center gap-1" data-testid={`assign-btn-${c.id}`}>
                  <Users size={14} /> Responsáveis
                </button>
              )}
              <Link to={`/contabilista/mensagens/${c.id}`} className="btn-secondary text-sm inline-flex items-center gap-1" data-testid={`messages-btn-${c.id}`}>
                <MessageCircle size={14} /> Mensagens
              </Link>
              <button onClick={() => openClients(c)} className="btn-secondary text-sm inline-flex items-center gap-1" data-testid={`clients-btn-${c.id}`}>
                <UserMinus size={14} /> Gerir utilizadores
              </button>
              <button onClick={() => openInvite(c)} className="btn-secondary text-sm inline-flex items-center gap-1" data-testid={`invite-btn-${c.id}`}>
                <Mail size={14} /> Convidar
              </button>
              <button onClick={() => setDeleteOpen(c)} className="p-2 rounded-lg hover:bg-[#FEE2E2] text-[#991B1B] transition-colors" data-testid={`delete-company-${c.id}`} title="Apagar">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Invite modal */}
      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 fade-in" onClick={() => setInviteOpen(null)}>
          <div className="card-soft w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">convidar utilizador</span>
              <button onClick={() => setInviteOpen(null)} className="p-1 rounded hover:bg-[color:var(--border)]"><X size={16} /></button>
            </div>
            <h2 className="font-display text-xl mt-2">{inviteOpen.name}</h2>
            {!inviteResult ? (
              <form onSubmit={sendInvite} className="mt-4 space-y-3">
                <div>
                  <label className="label-eyebrow block mb-2">Email do convidado</label>
                  <input type="email" className="input-soft" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required data-testid="invite-email-input" />
                </div>
                <button type="submit" disabled={inviteSending} className="btn-primary w-full" data-testid="send-invite-btn">
                  {inviteSending ? "A enviar email…" : "Enviar convite por email"}
                </button>
                <p className="text-xs text-[color:var(--ink-muted)]">O email será enviado a partir da sua conta Gmail configurada no separador Conta.</p>
              </form>
            ) : (
              <div className="mt-4 space-y-3" data-testid="invite-result">
                <div className="flex items-center gap-2 text-[#065F46]"><Check size={18}/> Convite enviado para {inviteEmail}</div>
                <div className="bg-[color:var(--paper)] border border-[color:var(--border)] rounded-lg p-3 text-xs break-all font-mono-data flex items-start gap-2">
                  <span className="flex-1">{inviteResult.invite_url}</span>
                  <button onClick={() => navigator.clipboard.writeText(inviteResult.invite_url)} className="p-1 hover:bg-[color:var(--border)] rounded" title="Copiar"><Copy size={14}/></button>
                </div>
                <button onClick={() => setInviteOpen(null)} className="btn-secondary w-full">Fechar</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assign accountants modal */}
      {assignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 fade-in" onClick={() => setAssignOpen(null)}>
          <div className="card-soft w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="assign-modal">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">contabilistas responsáveis</span>
              <button onClick={() => setAssignOpen(null)} className="p-1 rounded hover:bg-[color:var(--border)]"><X size={16} /></button>
            </div>
            <h2 className="font-display text-xl mt-2">{assignOpen.name}</h2>
            <p className="text-xs text-[color:var(--ink-muted)] mt-1">Selecione quem pode ver as faturas e mensagens desta empresa.</p>
            <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
              {accountants.length === 0 ? (
                <p className="text-sm text-[color:var(--ink-muted)]">Sem contabilistas. Crie um no separador Equipa.</p>
              ) : accountants.map((a) => (
                <label key={a.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[color:var(--paper)] cursor-pointer" data-testid={`assign-option-${a.id}`}>
                  <input
                    type="checkbox"
                    checked={assignSel.has(a.id)}
                    onChange={() => toggleAssign(a.id)}
                    className="w-4 h-4 accent-[color:var(--primary)]"
                    data-testid={`assign-checkbox-${a.id}`}
                  />
                  <span className="flex-1 text-sm">{a.name} {a.is_admin && <span className="text-xs text-[color:var(--ink-muted)]">(gestor)</span>}</span>
                  <span className="text-xs text-[color:var(--ink-muted)] font-mono-data">{a.email}</span>
                </label>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={saveAssign} disabled={assignSaving} className="btn-primary flex-1" data-testid="save-assign-btn">{assignSaving ? "A guardar…" : "Guardar"}</button>
              <button onClick={() => setAssignOpen(null)} className="btn-secondary">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Manage clients modal */}
      {clientsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 fade-in" onClick={() => setClientsOpen(null)}>
          <div className="card-soft w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="clients-modal">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">utilizadores da empresa</span>
              <button onClick={() => setClientsOpen(null)} className="p-1 rounded hover:bg-[color:var(--border)]"><X size={16} /></button>
            </div>
            <h2 className="font-display text-xl mt-2">{clientsOpen.name}</h2>
            <p className="text-xs text-[color:var(--ink-muted)] mt-1">Remova utilizadores que já não devem aceder a esta empresa. Depois de remover todos, poderá apagar a empresa.</p>

            <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer" data-testid="delete-invoices-toggle">
              <input type="checkbox" checked={delInvoices} onChange={(e) => setDelInvoices(e.target.checked)} className="w-4 h-4 accent-[#991B1B]" />
              Apagar também as faturas do utilizador ao remover
            </label>

            <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
              {clientsLoading ? (
                <p className="text-sm text-[color:var(--ink-muted)] py-4">A carregar…</p>
              ) : clientsList.length === 0 ? (
                <p className="text-sm text-[color:var(--ink-muted)] py-4" data-testid="no-clients">Esta empresa não tem utilizadores associados.</p>
              ) : clientsList.map((cl) => (
                <div key={cl.id} className="flex items-center gap-3 p-3 rounded-lg border border-[color:var(--border)]" data-testid={`client-row-${cl.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{cl.name || "—"}</div>
                    <div className="text-xs text-[color:var(--ink-muted)] font-mono-data truncate">{cl.email} · {cl.invoices_count} fatura(s)</div>
                  </div>
                  <button
                    onClick={() => removeClient(cl)}
                    disabled={removingId === cl.id}
                    className="btn-secondary text-sm inline-flex items-center gap-1 text-[#991B1B] hover:bg-[#FEE2E2]"
                    data-testid={`remove-client-${cl.id}`}
                  >
                    <Trash2 size={14} /> {removingId === cl.id ? "A remover…" : "Remover"}
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => setClientsOpen(null)} className="btn-secondary w-full mt-4" data-testid="close-clients-btn">Fechar</button>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 fade-in" onClick={() => !deleting && setDeleteOpen(null)}>
          <div className="card-soft w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="delete-company-modal">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">apagar empresa</span>
              <button onClick={() => setDeleteOpen(null)} className="p-1 rounded hover:bg-[color:var(--border)]"><X size={16} /></button>
            </div>
            <h2 className="font-display text-xl mt-2">{deleteOpen.name}</h2>
            <p className="text-sm text-[color:var(--ink-muted)] mt-2">
              Tem a certeza que quer apagar esta empresa? Esta ação é irreversível.
            </p>
            {deleteOpen.users_count > 0 && (
              <p className="text-xs text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-lg p-3 mt-3" data-testid="delete-blocked-warning">
                Esta empresa tem {deleteOpen.users_count} cliente(s) associado(s). Por segurança, remova os clientes antes de a apagar.
              </p>
            )}
            <div className="mt-5 flex gap-2">
              <button onClick={confirmDelete} disabled={deleting} className="btn-primary flex-1 bg-[#991B1B] hover:bg-[#7F1515] border-[#991B1B]" data-testid="confirm-delete-company-btn">
                {deleting ? "A apagar…" : "Apagar empresa"}
              </button>
              <button onClick={() => setDeleteOpen(null)} disabled={deleting} className="btn-secondary" data-testid="cancel-delete-company-btn">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
